import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Spin, Alert, Divider, Tag, Space, Row, Col, Statistic } from 'antd';
import { ArrowLeftOutlined, LinkOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import { useBackground } from '../contexts/BackgroundContext';

const { Title, Text, Paragraph } = Typography;

const PaperDetailModal = ({ visible, onClose, paper, onViewWithSCAI }) => {
  const [loading, setLoading] = useState(false);
  const [paperDetails, setPaperDetails] = useState(null);
  const [error, setError] = useState(null);
  const { currentTheme } = useBackground();

  useEffect(() => {
    if (visible && paper) {
      fetchPaperDetails();
    }
  }, [visible, paper]);

  const fetchPaperDetails = async () => {
    if (!paper?.doi) {
      setError('No DOI available for this paper');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const cleanDoi = paper.doi.replace(/^https?:\/\/doi\.org\//i, "");
      const response = await fetch(`https://api.openalex.org/works/doi:${cleanDoi}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch paper details: ${response.status}`);
      }
      
      const data = await response.json();
      setPaperDetails(data);
    } catch (error) {
      console.error('Error fetching paper details:', error);
      setError('Failed to load paper details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDOI = () => {
    if (paper?.doi) {
      const cleanDoi = paper.doi.replace(/^https?:\/\/doi\.org\//i, "");
      const doiUrl = `https://doi.org/${cleanDoi}`;
      window.open(doiUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleViewWithSCAI = () => {
    if (onViewWithSCAI && paper) {
      onViewWithSCAI(paper);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).getFullYear();
    } catch {
      return dateStr;
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ maxWidth: 1000 }}
      bodyStyle={{
        padding: 0,
        maxHeight: '80vh',
        overflow: 'auto',
        background: currentTheme.isDark ? '#1f1f1f' : '#ffffff'
      }}
    >
      <div style={{ 
        padding: '24px',
        background: currentTheme.isDark ? '#1f1f1f' : '#ffffff',
        color: currentTheme.isDark ? '#ffffff' : '#000000'
      }}>
        {/* Header with Back Button */}
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={onClose}
            type="text"
            style={{ 
              color: currentTheme.isDark ? '#40a9ff' : '#1890ff',
              border: 'none'
            }}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0, color: currentTheme.isDark ? '#ffffff' : '#000000' }}>
            Paper Details
          </Title>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: currentTheme.isDark ? '#ffffff' : '#666666' }}>
              Loading paper details...
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            style={{ marginBottom: '24px' }}
          />
        )}

        {!loading && !error && (
          <>
            {/* Paper Title */}
            <Title level={2} style={{ 
              marginBottom: '16px',
              color: currentTheme.isDark ? '#ffffff' : '#000000',
              lineHeight: 1.3
            }}>
              {paper?.title || paperDetails?.title}
            </Title>

            {/* Authors and Basic Info */}
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ 
                fontSize: '16px',
                color: currentTheme.isDark ? '#52c41a' : '#389e0d',
                display: 'block',
                marginBottom: '8px'
              }}>
                {paper?.author || paperDetails?.authorships?.map(a => a.author?.display_name).join(', ') || 'Unknown authors'}
              </Text>
              <Text style={{ 
                color: currentTheme.isDark ? 'rgba(255, 255, 255, 0.8)' : '#666',
                fontSize: '14px'
              }}>
                {formatDate(paper?.year || paperDetails?.publication_date)} • {paper?.location || paperDetails?.primary_location?.source?.display_name || 'Unknown journal'}
              </Text>
            </div>

            {/* DOI */}
            <div style={{ marginBottom: '24px' }}>
              <Text strong style={{ color: currentTheme.isDark ? '#ffffff' : '#000000' }}>DOI: </Text>
              <Text code style={{ 
                color: currentTheme.isDark ? '#40a9ff' : '#1890ff',
                fontSize: '14px'
              }}>
                {paper?.doi?.replace(/^https?:\/\/doi\.org\//i, "") || paperDetails?.doi?.replace('https://doi.org/', '')}
              </Text>
            </div>

            {/* Statistics */}
            {paperDetails && (
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Citations"
                    value={formatNumber(paperDetails.cited_by_count)}
                    valueStyle={{ color: currentTheme.isDark ? '#52c41a' : '#389e0d' }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="References"
                    value={formatNumber(paperDetails.referenced_works?.length)}
                    valueStyle={{ color: currentTheme.isDark ? '#40a9ff' : '#1890ff' }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Open Access"
                    value={paperDetails.open_access?.is_oa ? 'Yes' : 'No'}
                    valueStyle={{ 
                      color: paperDetails.open_access?.is_oa 
                        ? (currentTheme.isDark ? '#52c41a' : '#389e0d')
                        : (currentTheme.isDark ? '#ff7875' : '#f5222d')
                    }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Type"
                    value={paperDetails.type_crossref || paperDetails.type || 'Article'}
                    valueStyle={{ color: currentTheme.isDark ? '#ffffff' : '#000000' }}
                  />
                </Col>
              </Row>
            )}

            {/* Abstract */}
            {(paper?.abstract || paperDetails?.abstract_inverted_index) && (
              <>
                <Divider style={{ borderColor: currentTheme.isDark ? '#434343' : '#f0f0f0' }} />
                <Title level={4} style={{ color: currentTheme.isDark ? '#ffffff' : '#000000' }}>
                  Abstract
                </Title>
                <Paragraph style={{ 
                  color: currentTheme.isDark ? 'rgba(255, 255, 255, 0.9)' : '#333',
                  lineHeight: 1.6,
                  textAlign: 'justify'
                }}>
                  {paper?.abstract?.replace('Abstract', '') || 'Abstract not available'}
                </Paragraph>
              </>
            )}

            {/* Concepts/Keywords */}
            {paperDetails?.concepts && paperDetails.concepts.length > 0 && (
              <>
                <Divider style={{ borderColor: currentTheme.isDark ? '#434343' : '#f0f0f0' }} />
                <Title level={4} style={{ color: currentTheme.isDark ? '#ffffff' : '#000000' }}>
                  Research Areas
                </Title>
                <Space wrap>
                  {paperDetails.concepts.slice(0, 10).map((concept, index) => (
                    <Tag 
                      key={index}
                      color={currentTheme.isDark ? 'blue' : 'geekblue'}
                      style={{ marginBottom: '8px' }}
                    >
                      {concept.display_name} ({Math.round(concept.score * 100)}%)
                    </Tag>
                  ))}
                </Space>
              </>
            )}

            {/* Action Buttons */}
            <Divider style={{ borderColor: currentTheme.isDark ? '#434343' : '#f0f0f0' }} />
            <Space size="middle" wrap>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={handleOpenDOI}
                style={{
                  background: currentTheme.isDark ? '#1890ff' : '#1890ff',
                  borderColor: currentTheme.isDark ? '#1890ff' : '#1890ff'
                }}
              >
                Open DOI
              </Button>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleViewWithSCAI}
                style={{
                  background: currentTheme.isDark ? '#52c41a' : '#52c41a',
                  borderColor: currentTheme.isDark ? '#52c41a' : '#52c41a'
                }}
              >
                View With SCAI
              </Button>
            </Space>
          </>
        )}
      </div>
    </Modal>
  );
};

export default PaperDetailModal;
